// src/components/modals/LeadIntelModals.jsx
import React, { useState } from 'react';
import { FiX, FiClock, FiDatabase, FiActivity, FiUser } from 'react-icons/fi';
import { motion } from 'framer-motion';

// --- BASE DO MODAL ---
const ModalOverlay = ({ children, title, onClose, icon: Icon }) => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1000] flex items-center justify-center p-6 bg-black/60 backdrop-blur-md">
        <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            className="w-full max-w-2xl border rounded-[3rem] overflow-hidden shadow-2xl">
            <header style={{ borderColor: 'var(--border-color)', backgroundColor: 'rgba(255,255,255,0.03)' }}
                className="px-10 py-8 border-b flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <div style={{ backgroundColor: 'color-mix(in srgb, var(--accent-color), transparent 90%)', color: 'var(--accent-color)' }}
                        className="p-3 rounded-2xl"><Icon size={20} /></div>
                    <h2 className="text-xl font-black uppercase italic tracking-tighter">{title}</h2>
                </div>
                <button onClick={onClose} style={{ color: 'var(--text-secondary)' }}
                    className="p-3 hover:bg-black/10 rounded-full transition-all"><FiX size={24} /></button>
            </header>
            <div className="p-10 max-h-[70vh] overflow-y-auto no-scrollbar">{children}</div>
        </motion.div>
    </motion.div>
);

// --- 📜 HISTÓRICO ---
export const LeadHistoryModal = ({ lead, onClose }) => (
    <ModalOverlay title="Linha do Tempo (Lead)" icon={FiClock} onClose={onClose}>
        <div className="space-y-8 relative">
            <div className="absolute left-[19px] top-2 bottom-2 w-[2px]" style={{ backgroundColor: 'var(--border-color)', opacity: 0.2 }} />
            {lead.historico && lead.historico.length > 0 ? lead.historico.map((log, i) => (
                <div key={i} className="relative pl-12">
                    <div style={{ backgroundColor: 'var(--bg-main)', borderColor: 'var(--border-color)' }}
                        className="absolute left-0 top-1 w-10 h-10 rounded-full border flex items-center justify-center z-10">
                        <div style={{ backgroundColor: 'var(--accent-color)' }} className="w-3 h-3 rounded-full animate-pulse" />
                    </div>
                    <div style={{ backgroundColor: 'rgba(255,255,255,0.02)', borderColor: 'var(--border-color)' }}
                        className="p-6 rounded-3xl border space-y-3">
                        <div className="flex justify-between items-center">
                            <span style={{ color: 'var(--accent-color)' }} className="text-xs font-black uppercase tracking-widest">{log.fase}</span>
                            <span style={{ color: 'var(--text-secondary)' }} className="text-[10px] opacity-60 font-bold">{new Date(log.data).toLocaleString('pt-BR')}</span>
                        </div>
                        <p style={{ color: 'var(--text-primary)' }} className="text-[11px] leading-relaxed opacity-90 italic">"{log.comentario || 'Sem observações.'}"</p>
                        <p className="text-[8px] opacity-40 uppercase text-right mt-2">Por: {log.alteradoPor}</p>
                    </div>
                </div>
            )) : (
                <div className="text-center opacity-50 py-10 uppercase font-bold text-xs">Nenhum histórico registrado.</div>
            )}
        </div>
    </ModalOverlay>
);

// --- 📊 LOGS ---
export const LeadLogModal = ({ lead, onClose }) => (
    <ModalOverlay title="Logs Técnicos" icon={FiDatabase} onClose={onClose}>
        <div style={{ backgroundColor: 'var(--bg-main)', borderColor: 'var(--border-color)' }} className="rounded-3xl p-6 border font-mono text-[10px] overflow-x-auto">
            <pre style={{ color: 'var(--accent-color)' }}>{JSON.stringify(lead, null, 4)}</pre>
        </div>
    </ModalOverlay>
);

// --- 🚦 UPDATE STATUS ---
export const StatusUpdateLeadModal = ({ onClose, onConfirm, newStatus, currentStatus }) => {
    const [comentario, setComentario] = useState('');
    const [operador, setOperador] = useState('');

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-lg">
            <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
                style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                className="w-full max-w-xl border rounded-[3rem] p-10 shadow-3xl">
                
                <header className="flex justify-between items-center mb-8">
                    <div className="flex items-center gap-4">
                        <div style={{ backgroundColor: 'color-mix(in srgb, var(--accent-color), transparent 90%)', color: 'var(--accent-color)' }} className="p-3 rounded-2xl"><FiActivity size={20} /></div>
                        <h2 className="text-xl font-black uppercase italic tracking-tighter">Avanço de Funil</h2>
                    </div>
                    <button onClick={onClose} style={{ color: 'var(--text-secondary)' }}><FiX size={20}/></button>
                </header>

                <div className="mb-6 space-y-4">
                    <div className="flex justify-between items-center p-4 bg-black/20 border border-white/5 rounded-2xl">
                        <span className="text-[10px] uppercase opacity-50 font-black">Fase Atual</span>
                        <span className="text-xs uppercase font-black">{currentStatus}</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-[var(--accent-color)]/10 border border-[var(--accent-color)]/30 rounded-2xl">
                        <span className="text-[10px] uppercase text-[var(--accent-color)] opacity-70 font-black">Nova Fase</span>
                        <span className="text-xs uppercase font-black text-[var(--accent-color)]">{newStatus}</span>
                    </div>
                </div>

                <div className="space-y-4 mb-8">
                    <input 
                        type="text" 
                        value={operador} 
                        onChange={(e) => setOperador(e.target.value)} 
                        placeholder="Nome do Operador (Opcional)"
                        className="w-full h-14 px-6 rounded-2xl bg-black/40 border border-white/10 text-white text-sm outline-none focus:border-accent transition-all font-medium" 
                    />
                    
                    <textarea autoFocus value={comentario} onChange={(e) => setComentario(e.target.value)}
                        placeholder="Descreva o motivo da mudança de fase..."
                        className="w-full h-32 p-6 rounded-[2rem] bg-black/40 border border-white/10 text-white text-sm outline-none focus:border-accent transition-all resize-none placeholder:opacity-30 font-medium" 
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <button onClick={onClose} style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                        className="h-14 rounded-2xl border font-black uppercase text-[10px] tracking-widest hover:bg-black/5">Cancelar</button>
                    <button onClick={() => onConfirm(comentario, operador)} disabled={!comentario.trim()}
                        style={{ backgroundColor: 'var(--accent-color)' }}
                        className="h-14 rounded-2xl text-black font-black uppercase text-[10px] tracking-widest active:scale-95 disabled:opacity-20">Confirmar</button>
                </div>
            </motion.div>
        </motion.div>
    );
};
